import { useEffect, useSyncExternalStore, type WheelEvent } from 'react';
import { previewStore } from '../preview/previewStore';
import { FEATURE_COLORS, FEATURE_LABELS, FEATURE_TYPES } from '../slice/types';
import { formatDuration } from '../export/estimate';
import { useAppStore } from '../store/appStore';

export function usePreviewState() {
  return useSyncExternalStore(previewStore.subscribe, previewStore.getSnapshot, previewStore.getSnapshot);
}

export function PreviewControls() {
  const preview = usePreviewState();
  const settings = useAppStore((s) => s.settings);
  const layers = preview.layers;
  const max = Math.max(0, layers.length - 1);
  const layer = layers[preview.currentLayer];
  const z = layer ? layer.z : 0;

  useEffect(() => {
    if (!preview.playing) return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      raf = requestAnimationFrame(tick);
      if (now - last < 90) return;
      last = now;
      const s = previewStore.getSnapshot();
      if (s.currentLayer >= Math.max(0, s.layers.length - 1)) {
        previewStore.set({ playing: false });
        return;
      }
      previewStore.step(1);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [preview.playing]);

  const totalLen = layers.reduce((a, l) => {
    let s = 0;
    for (const k of FEATURE_TYPES) s += (l.lengths[k] || 0);
    return a + s;
  }, 0);
  const uptoLen = layers.slice(0, preview.currentLayer + 1).reduce((a, l) => {
    let s = 0;
    for (const k of FEATURE_TYPES) s += (l.lengths[k] || 0);
    return a + s;
  }, 0);
  const estMin = preview.estimate?.approxMinutes ?? 0;
  const untilMin = totalLen > 0 && estMin ? Math.max(1, Math.round(estMin * (uptoLen / totalLen))) : 0;

  function onSliderWheel(e: WheelEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    previewStore.step(e.deltaY > 0 ? -1 : 1);
  }

  return (
    <div className="preview-chrome" data-testid="preview-chrome">
      <div className="preview-caption">Preview – final slicing by Bambu cloud</div>

      {preview.status === 'slicing' && (
        <div className="slice-progress" data-testid="slice-progress">
          <div className="slice-progress-label">{preview.message || `Slicing… ${preview.progress}%`}</div>
          <div className="slice-progress-bar">
            <span style={{ width: `${preview.progress}%` }} />
          </div>
        </div>
      )}
      {preview.status === 'error' && (
        <div className="slice-progress error">{preview.error || 'Slice failed'}</div>
      )}

      <div className="layer-rail" data-testid="layer-slider" onWheel={onSliderWheel}>
        <button type="button" className="layer-step" aria-label="Layer up" data-testid="layer-up" onClick={() => previewStore.step(1)}>+</button>
        <input
          type="range"
          min={0}
          max={max}
          step={1}
          value={Math.min(preview.currentLayer, max)}
          aria-label="Layer"
          onChange={(e) => previewStore.setLayer(Number(e.target.value))}
        />
        <button type="button" className="layer-step" aria-label="Layer down" data-testid="layer-down" onClick={() => previewStore.step(-1)}>−</button>
      </div>

      <div className="layer-readout" data-testid="layer-readout">
        Layer {layers.length ? preview.currentLayer + 1 : 0} / {layers.length} · Z {z.toFixed(2)} mm
      </div>

      <div className="layer-scrub" data-testid="layer-scrub">
        <button
          type="button"
          className="play-btn"
          data-testid="layer-play"
          aria-label={preview.playing ? 'Pause' : 'Play layers'}
          onClick={() => previewStore.set({ playing: !preview.playing })}
        >
          {preview.playing ? '❚❚' : '▶'}
        </button>
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(preview.layerDraw * 100)}
          aria-label="Draw progress"
          onChange={(e) => previewStore.set({ layerDraw: Number(e.target.value) / 100 })}
        />
      </div>

      <div className="preview-legend" data-testid="preview-legend">
        <label className="color-by">
          Colour by
          <select
            value={preview.colorBy}
            onChange={(e) => previewStore.set({ colorBy: e.target.value as 'feature' | 'filament' })}
          >
            <option value="feature">Feature</option>
            <option value="filament">Filament</option>
          </select>
        </label>
        {FEATURE_TYPES.map((f) => (
          <button
            key={f}
            type="button"
            className={`legend-item ${preview.hidden[f] ? 'off' : ''}`}
            onClick={() => previewStore.toggleFeature(f)}
          >
            <i style={{ background: preview.colorBy === 'filament' ? String(settings.filament_colour || '#E8C547') : `#${FEATURE_COLORS[f].toString(16).padStart(6, '0')}` }} />
            {FEATURE_LABELS[f]}
          </button>
        ))}
        <div className="legend-time">
          {preview.estimate
            ? `${formatDuration(untilMin || preview.estimate.approxMinutes)} to this layer`
            : 'Estimating…'}
        </div>
      </div>
    </div>
  );
}
